/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/rules-of-hooks */
import { addListener, onlyCallOnceInOneTick } from '@web3/utils';
import { EventEmitter } from 'eventemitter3';
import { useEffect, useState } from 'react';

const InitFlag = Symbol('initFlag');

export class EventBasedStore<
  T extends Record<string, unknown>,
  Computed extends Record<string, unknown> = Record<string, unknown>,
> extends EventEmitter<{
  update: [T & Computed];
}> {
  protected _origin: T;
  protected _state: T;
  protected _compute?: (state: T) => Computed;
  private _init?: () => void;
  private [InitFlag] = false;
  constructor(state: T, compute?: (state: T) => Computed, init?: () => void) {
    super();
    this._compute = compute;
    this._origin = state;
    this._state = state;
    this._init = init;
  }
  private _getState() {
    this.init();
    const state = this._state;
    const computed = this._compute?.(state) ?? ({} as Computed);
    const merged = { ...state, ...computed } as T & Computed;
    return {
      state,
      computed,
      merged,
    };
  }
  private _presentState?: { state: T; computed: Computed; merged: Computed & T };
  get state() {
    if (this._presentState?.state === this._state) {
      return this._presentState.merged;
    }
    this._presentState = this._getState();
    return this._presentState.merged;
  }

  protected _triggerUpdate = onlyCallOnceInOneTick(() => {
    this.emit('update', this.state);
  });

  public init() {
    if (this[InitFlag]) {
      return;
    }
    this[InitFlag] = true;
    // 首次use，触发执行一个初始化，适用于一些接口型全局状态的延迟加载，又只加载一次。
    if (this._init) {
      const init = this._init;
      this._init = undefined;
      init();
    }
  }

  setState(state: Partial<T>): void;
  setState<Key extends keyof T>(key: Key, value: T[Key]): void;
  setState(key: unknown, value?: unknown): void {
    if (!key) {
      return;
    }
    if (typeof key === 'string') {
      // 传入的key value形式
      this._state = {
        ...this._state,
        [key]: value,
      };
      this._triggerUpdate();
      return;
    }
    if (typeof key === 'object') {
      // 这里就模糊一下，不考虑 是array 的情况
      // 这里也先不考虑用户 故意设置 新对象属性为 undefined的情况
      this._state = {
        ...this._state,
        ...key,
      };
      this._triggerUpdate();
    }
  }

  reset(state?: T) {
    if (state) {
      this._state = state;
    } else {
      this._state = this._origin;
    }
    this._triggerUpdate();
  }

  useStoreState() {
    const [, update] = useState(1);
    this.init();
    useEffect(() => {
      // update 触发组件更新
      return addListener(this, 'update', () => update((n) => n + 1));
    }, []);
    return this.state;
  }

  invokeWithState(fn: (state: T & Computed) => void): () => void {
    fn(this.state);
    return addListener(this, 'update', fn);
  }
}

type Methods<T> = {
  [K in keyof T]: T[K] extends (...args: any[]) => any ? T[K] : never;
};

/**
 * 从store对象上选取方法属性做成一个actions对象
 * @param store store 实例
 * @param keys
 * @returns actions
 *
 */
export const pickStoreActions = <T extends EventBasedStore<any>, AK extends keyof Methods<T>>(store: T, keys: AK[]) => {
  return keys.reduce(
    (acc, key) => {
      acc[key] = (store[key] as Function).bind(store);
      return acc;
    },
    {
      setState: store.setState.bind(store),
    } as Pick<Methods<T>, AK | 'setState'>,
  );
};

export type GetStoreState<T> = T extends EventBasedStore<any> ? T['state'] : never;
